/**
 * SuppressionRules.js
 * 
 * Centralized suppression logic for DASH data visualization.
 * Detects and handles:
 * - Suppression codes (111111, 333333, 555555)
 * - Confidence intervals with no statistical certainty (0%–100%)
 */

/**
 * Check if a value is a special suppression code
 * @param {*} v - The value to check
 * @returns {boolean} - True if value is a suppression code
 */
export function isSpecialCode(v) {
  if (v === null || v === undefined) return false;
  const s = String(v).trim();
  return s === "111111" || s === "333333" || s === "555555";
}

/**
 * Parse a numeric value, handling various input formats
 * @param {*} v - The value to parse
 * @returns {number|null} - Parsed number or null if invalid
 */
export function parseNumber(v) {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  if (!isNaN(n)) return n;
  // Try to parse ranges like "(3.4-6.2)" or "3.4 - 6.2"
  const m = String(v).match(/([0-9]+\.?[0-9]*)[^0-9]+([0-9]+\.?[0-9]*)/);
  if (m) return (Number(m[1]) + Number(m[2])) / 2;
  // Fallback: extract the first numeric token (handles values like "100%" or "~7")
  const first = String(v).match(/[+-]?\d*\.?\d+/);
  if (first && first[0] !== "") {
    const f = Number(first[0]);
    if (!isNaN(f)) return f;
  }
  return null;
}

/**
 * Check if a confidence interval represents no statistical certainty
 * (i.e., 0%–100% range, indicating uncertainty across the entire spectrum)
 * @param {number} lower - Lower bound of CI
 * @param {number} upper - Upper bound of CI
 * @param {number} tolerance - Tolerance for rounding (default 0.5 percentage points)
 * @returns {boolean} - True if CI is effectively 0–100
 */
export function isZeroToHundredCI(lower, upper, tolerance = 0.5) {
  if (lower === null || lower === undefined || upper === null || upper === undefined) {
    return false;
  }
  
  const lowerNum = Number(lower);
  const upperNum = Number(upper);
  
  if (isNaN(lowerNum) || isNaN(upperNum)) {
    return false;
  }
  
  // Check if CI spans approximately 0 to 100
  return (lowerNum <= tolerance) && (upperNum >= (100 - tolerance));
}

/**
 * Check if a row's confidence interval should be suppressed
 * Suppression occurs when:
 * 1. Data value is a suppression code (111111, 333333, 555555)
 * 2. CI is effectively 0%–100% (no statistical certainty)
 * @param {object} row - Data row with CI bounds
 * @param {string|number} rowDataValue - The primary data value to check for codes
 * @returns {boolean} - True if CI should be suppressed
 */
export function shouldSuppressCIRange(row, rowDataValue = null) {
  if (!row) return false;
  
  // First check for suppression codes in the data value
  const dataVal = rowDataValue ?? row.DataValue ?? row.DataValueRaw ?? row.NPercent;
  if (isSpecialCode(dataVal)) {
    return true;
  }
  
  // Check if CI bounds indicate no statistical certainty (0–100 range)
  // Try to extract CI bounds from various field names
  let lower = null;
  let upper = null;
  
  const candidatesLower = [
    row.LowCI, row.LowValue, row.LowerValue, row.LCI, row.Lower,
    row.LowerBound, row.ConfidenceLower
  ];
  
  const candidatesUpper = [
    row.HighCI, row.HighValue, row.UpperValue, row.UCI, row.Upper,
    row.UpperBound, row.ConfidenceUpper
  ];
  
  // Extract lower bound
  for (let c of candidatesLower) {
    if (c !== undefined && c !== null && String(c).trim() !== '') {
      const parsed = parseNumber(c);
      if (parsed !== null && !isNaN(parsed)) {
        lower = parsed;
        break;
      }
    }
  }
  
  // Extract upper bound
  for (let c of candidatesUpper) {
    if (c !== undefined && c !== null && String(c).trim() !== '') {
      const parsed = parseNumber(c);
      if (parsed !== null && !isNaN(parsed)) {
        upper = parsed;
        break;
      }
    }
  }
  
  // Also try parsing from ConfidenceInterval string (e.g., "0.0 - 100.0")
  if ((lower === null || upper === null) && row.ConfidenceInterval) {
    const ciStr = String(row.ConfidenceInterval);
    const match = ciStr.match(/([0-9]+\.?[0-9]*)[^0-9]+([0-9]+\.?[0-9]*)/);
    if (match) {
      if (lower === null) lower = Number(match[1]);
      if (upper === null) upper = Number(match[2]);
    }
  }
  
  // If we have both bounds, check if it's effectively 0–100
  if (lower !== null && upper !== null) {
    return isZeroToHundredCI(lower, upper);
  }
  
  return false;
}

/**
 * Format a confidence interval string, checking for suppression
 * @param {number} lower - Lower bound
 * @param {number} upper - Upper bound
 * @param {object} row - Full data row (for suppression detection)
 * @param {string|number} rowDataValue - Primary data value
 * @param {boolean} suppressZeroToHundred - If true, suppress 0–100 ranges
 * @returns {string} - Formatted CI or suppression message
 */
export function formatCIWithSuppression(lower, upper, row = null, rowDataValue = null, suppressZeroToHundred = true) {
  // Check for missing data
  if (lower === null || lower === undefined || upper === null || upper === undefined) {
    return "—";
  }
  
  const lowerNum = Number(lower);
  const upperNum = Number(upper);
  
  if (isNaN(lowerNum) || isNaN(upperNum)) {
    return "—";
  }
  
  // Check for suppression
  if (suppressZeroToHundred && isZeroToHundredCI(lowerNum, upperNum)) {
    return null; // null signals suppression; caller should handle display
  }
  
  // Format normally
  return `${lowerNum.toFixed(1)} - ${upperNum.toFixed(1)}`;
}

/**
 * Get the suppression footnote text
 * @returns {string} - Exact footnote text for 0–100 CI suppression
 */
export function getSuppressionFootnoteText() {
  return "Estimate suppressed: Confidence interval (0%–100%) represents no statistical certainty.";
}

/**
 * Get the suppression footnote symbol
 * @returns {string} - Symbol to display (e.g., superscript number)
 */
export function getSuppressionFootnoteSymbol() {
  return "†"; // dagger symbol, or can be a superscript number like "1"
}

/**
 * Check if a row has any suppressed CI ranges
 * Useful for determining if footnote should be displayed
 * @param {array} rows - Array of data rows
 * @returns {boolean} - True if any row has suppressed CI
 */
export function hasSuppressionInRows(rows) {
  if (!Array.isArray(rows)) return false;
  return rows.some(row => shouldSuppressCIRange(row));
}

/**
 * Detect if any rows contain suppression codes (111111, 333333, 555555)
 * @param {array} rows - Array of data rows
 * @returns {boolean} - True if any row contains a suppression code
 */
export function hasAnySuppressionCode(rows) {
  if (!Array.isArray(rows)) return false;
  return rows.some(row => {
    if (!row) return false;
    const val = row.DataValue ?? row.DataValueRaw ?? row.NPercent;
    return isSpecialCode(val);
  });
}

/**
 * Detect if any rows have 0-100 CI (no statistical certainty)
 * @param {array} rows - Array of data rows
 * @returns {boolean} - True if any row has 0-100 CI range
 */
export function hasZeroHundredSuppressionInRows(rows) {
  if (!Array.isArray(rows)) return false;
  return rows.some(row => {
    if (!row) return false;
    // Check for suppression codes first
    const val = row.DataValue ?? row.DataValueRaw ?? row.NPercent;
    if (isSpecialCode(val)) return false;
    // Check for 0-100 CI
    return shouldSuppressCIRange(row);
  });
}

/**
 * Get all conditional footnotes that should appear based on data
 * Conditional footnotes only show when their data is present
 * @param {array} rows - Array of data rows
 * @returns {object} - Object with flags for each conditional footnote
 */
export function getConditionalFootnotes(rows) {
  const result = {
    hasEmDash: false,      // Em-dash for missing data
    hasNACode: false,      // N/A for suppression codes
    hasDashCI: false,      // Dash for missing CI (non-0/100 values)
    hasZeroHundredCI: false // 0-100 CI suppression
  };

  if (!Array.isArray(rows)) return result;

  rows.forEach(row => {
    if (!row) return;
    
    const val = row.DataValue ?? row.DataValueRaw ?? row.NPercent;
    const numVal = parseNumber(val);
    
    // Check for suppression codes → N/A
    if (isSpecialCode(val)) {
      result.hasNACode = true;
      return;
    }
    
    // Check for missing data → Em-dash
    if (val === null || val === undefined || val === '') {
      result.hasEmDash = true;
      return;
    }
    
    // Check for 0-100 CI suppression
    if (shouldSuppressCIRange(row)) {
      result.hasZeroHundredCI = true;
      return;
    }
    
    // Check for missing CI (for non-0/100 values) → Dash
    if (numVal !== null && numVal !== 0 && numVal !== 100) {
      const ci = row.ConfidenceInterval;
      const ciStr = ci !== undefined && ci !== null ? String(ci).trim() : "";
      const ciIsDash = ciStr === "—" || ciStr === "-" || ciStr === "--" || ciStr === "–" || ciStr === "";
      if (ciIsDash || !ci) {
        result.hasDashCI = true;
      }
    }
  });

  return result;
}

/**
 * Get footnote text for display
 * @returns {object} - Object with all footnote texts indexed by key
 */
export function getAllFootnoteTexts() {
  return {
    pValue: "* Statistical significance is represented by a p-value of less than 0.05.",
    emDash: "— The number of respondents did not meet the minimum reporting threshold or data were not available.",
    naCode: "N/A = Confidence intervals not calculated for census sites and are not applicable for 0 percent or 100 percent.",
    zeroHundredCI: "† Estimate suppressed: Confidence interval (0%–100%) represents no statistical certainty.",
    dashCI: "— The number of respondents did not meet the minimum reporting threshold or data were not available.",
    ci: "CI = 95% Confidence Interval which indicates that 95% of the time, the value is expected to fall within this estimated range."
  };
}

/**
 * Detect census site via Y/N flags on row or nested Location
 */
export function isCensusSite(row) {
  if (!row) return false;
  // Check multiple possible field names for census flag
  const candidates = [
    row.Census,
    row.CensusSite,
    row.CensusSiteFlag,
    row.CensusSiteYN,
    row.LocationCensus,
    row.IsCensus,
    row.IsCensusSite,
    row.CensusFlag,
    row.IsCensussite,
    row.Location?.Census,
    row.Location?.CensusSite,
    row.Location?.CensusSiteFlag,
    row.Location?.IsCensus,
    row.Location?.LocationCensus,
    row.GeoLocation?.Census,
    row.GeoLocation?.CensusSite,
    row.GeoLocation?.CensusSiteFlag,
    row.GeoLocation?.LocationCensus,
    row.Location?.GeoLocation?.Census,
    row.Location?.GeoLocation?.CensusSite,
    row.Location?.GeoLocation?.CensusSiteFlag,
    row.Location?.GeoLocation?.LocationCensus
  ];
  for (const v of candidates) {
    if (v !== null && v !== undefined) {
      const str = String(v).trim().toUpperCase();
      if (str === "Y" || str === "YES" || str === "TRUE" || str === "1") return true;
    }
  }
  return false;
}

/**
 * Detect if any rows are census sites
 */
export function hasAnyCensus(rows) {
  if (!Array.isArray(rows)) return false;
  return rows.some(r => isCensusSite(r) || isCensusSite(r?.Location) || isCensusSite(r?.YearA) || isCensusSite(r?.YearB));
}
